import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { FaWeight,FaUtensils, FaFire, FaTint, FaCalendarAlt, FaChartLine, FaChartBar } from 'react-icons/fa';
import styled from 'styled-components';

const Container = styled.div`
  padding: 1rem;
  max-width: 1400px;
  margin: 0 auto;
  background: #f8f9fa;
  min-height: 100vh;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1rem;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ControlsCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (min-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 2rem;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  background: #f1f3f4;
  border-radius: 8px;
  padding: 4px;
  width: 100%;
  
  @media (min-width: 576px) {
    width: auto;
  }
`;

const ToggleButton = styled.button`
  background: ${props => props.active ? '#007bff' : 'transparent'};
  color: ${props => props.active ? 'white' : '#666'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  
  @media (min-width: 576px) {
    flex: auto;
  }
  
  &:hover {
    background: ${props => props.active ? '#0056b3' : '#e9ecef'};
  }
`;

const DateControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  
  @media (min-width: 576px) {
    flex-direction: row;
    align-items: center;
    width: auto;
  }
`;

const DateLabel = styled.label`
  margin-right: 0.5rem;
  font-weight: 500;
`;

const DateRangeInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  width: 100%;
  
  @media (min-width: 576px) {
    width: auto;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  padding: 1rem;
  text-align: center;
  border-left: 4px solid ${props => props.color || '#007bff'};
  
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const StatIcon = styled.div`
  font-size: 1.5rem;
  color: ${props => props.color || '#007bff'};
  margin-bottom: 0.5rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 0.25rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.8rem;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
    margin-bottom: 2rem;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  padding: 1rem;
  height: 100%;
  
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const ChartTitle = styled.h3`
  color: #333;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1rem;
    font-size: 1.25rem;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 250px;
  
  @media (min-width: 768px) {
    height: 300px;
  }
`;

const LoadingDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #666;
`;

const ErrorDiv = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14'];

const ProgressDashboard = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewType, setViewType] = useState('daily'); // 'daily' or 'monthly'
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [progressData, setProgressData] = useState([]);
  const [stats, setStats] = useState({
    avgWeight: 0,
    avgCalories: 0,
    avgWater: 0,
    totalDays: 0
  });

  useEffect(() => {
    if (currentUser) {
      loadProgressData();
    }
  }, [currentUser, dateRange, viewType]);

  const loadProgressData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError('');

      const progressRef = collection(db, 'users', currentUser.uid, 'dailyProgress');
      const q = query(
        progressRef,
        where('date', '>=', dateRange.start),
        where('date', '<=', dateRange.end),
        orderBy('date', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      const data = [];
      
      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        data.push({
          date: docData.date,
          weight: parseFloat(docData.weight) || 0,
          totalCalories: docData.totalCalories || 0,
          waterIntake: parseFloat(docData.waterIntake) || 0,
          breakfast: docData.meals?.breakfast?.calories ? parseInt(docData.meals.breakfast.calories) : 0,
          lunch: docData.meals?.lunch?.calories ? parseInt(docData.meals.lunch.calories) : 0,
          dinner: docData.meals?.dinner?.calories ? parseInt(docData.meals.dinner.calories) : 0,
          formattedDate: new Date(docData.date).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          })
        });
      });

      if (viewType === 'monthly') {
        setProgressData(processMonthlyData(data));
      } else {
        setProgressData(data);
      }

      calculateStats(data);
      
    } catch (err) {
      console.error('Error loading progress data:', err);
      setError('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  const processMonthlyData = (data) => {
    const monthlyData = {};
    
    data.forEach(day => {
      const month = new Date(day.date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
      
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month,
          totalWeight: 0,
          totalCalories: 0,
          totalWater: 0,
          totalBreakfast: 0,
          totalLunch: 0,
          totalDinner: 0,
          count: 0
        };
      }
      
      monthlyData[month].totalWeight += day.weight;
      monthlyData[month].totalCalories += day.totalCalories;
      monthlyData[month].totalWater += day.waterIntake;
      monthlyData[month].totalBreakfast += day.breakfast;
      monthlyData[month].totalLunch += day.lunch;
      monthlyData[month].totalDinner += day.dinner;
      monthlyData[month].count++;
    });
    
    return Object.values(monthlyData).map(month => ({
      date: month.month,
      weight: parseFloat((month.totalWeight / month.count).toFixed(1)),
      totalCalories: Math.round(month.totalCalories / month.count),
      waterIntake: parseFloat((month.totalWater / month.count).toFixed(1)),
      breakfast: Math.round(month.totalBreakfast / month.count),
      lunch: Math.round(month.totalLunch / month.count),
      dinner: Math.round(month.totalDinner / month.count),
      formattedDate: month.month
    }));
  };

  const calculateStats = (data) => {
    if (data.length === 0) {
      setStats({ avgWeight: 0, avgCalories: 0, avgWater: 0, totalDays: 0 });
      return;
    }

    const totals = data.reduce((acc, day) => ({
      weight: acc.weight + day.weight,
      calories: acc.calories + day.totalCalories,
      water: acc.water + day.waterIntake
    }), { weight: 0, calories: 0, water: 0 });

    setStats({
      avgWeight: parseFloat((totals.weight / data.length).toFixed(1)),
      avgCalories: Math.round(totals.calories / data.length),
      avgWater: parseFloat((totals.water / data.length).toFixed(1)),
      totalDays: data.length
    });
  };

  const getMealDistributionData = () => {
    if (progressData.length === 0) return [];
    
    const totals = progressData.reduce((acc, day) => ({
      breakfast: acc.breakfast + (day.breakfast || 0),
      lunch: acc.lunch + (day.lunch || 0),
      dinner: acc.dinner + (day.dinner || 0)
    }), { breakfast: 0, lunch: 0, dinner: 0 });

    return [
      { name: 'Breakfast', value: totals.breakfast, color: COLORS[0] },
      { name: 'Lunch', value: totals.lunch, color: COLORS[1] },
      { name: 'Dinner', value: totals.dinner, color: COLORS[2] }
    ].filter(item => item.value > 0);
  };

  if (loading) {
    return (
      <Container>
        <LoadingDiv>Loading your progress data...</LoadingDiv>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Progress Dashboard</Title>
        <Subtitle>Track your health and nutrition journey</Subtitle>
      </Header>

      <ControlsCard>
        <div>
          <DateLabel>View:</DateLabel>
          <ViewToggle>
            <ToggleButton 
              active={viewType === 'daily'} 
              onClick={() => setViewType('daily')}
            >
              <FaCalendarAlt style={{ marginRight: '0.5rem' }} />
              Daily
            </ToggleButton>
            <ToggleButton 
              active={viewType === 'monthly'} 
              onClick={() => setViewType('monthly')}
            >
              <FaChartBar style={{ marginRight: '0.5rem' }} />
              Monthly
            </ToggleButton>
          </ViewToggle>
        </div>
        
        <DateControl>
          <DateLabel>From:</DateLabel>
          <DateRangeInput
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
          />
        </DateControl>
        
        <DateControl>
          <DateLabel>To:</DateLabel>
          <DateRangeInput
            type="date"
            value={dateRange.end}
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
          />
        </DateControl>
      </ControlsCard>

      {error && <ErrorDiv>{error}</ErrorDiv>}

      {/* Stats Cards */}
      <StatsGrid>
        <StatCard color="#007bff">
          <StatIcon color="#007bff"><FaWeight /></StatIcon>
          <StatValue>{stats.avgWeight} kg</StatValue>
          <StatLabel>Average Weight</StatLabel>
        </StatCard>
        
        <StatCard color="#28a745">
          <StatIcon color="#28a745"><FaFire /></StatIcon>
          <StatValue>{stats.avgCalories}</StatValue>
          <StatLabel>Average Calories</StatLabel>
        </StatCard>
        
        <StatCard color="#17a2b8">
          <StatIcon color="#17a2b8"><FaTint /></StatIcon>
          <StatValue>{stats.avgWater}</StatValue>
          <StatLabel>Average Water Intake</StatLabel>
        </StatCard>
        
        <StatCard color="#ffc107">
          <StatIcon color="#ffc107"><FaChartLine /></StatIcon>
          <StatValue>{stats.totalDays}</StatValue>
          <StatLabel>Days Tracked</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* Charts */}
      <ChartsGrid>
        {/* Weight Progress Chart */}
        <ChartCard>
          <ChartTitle>
            <FaWeight style={{ color: '#007bff' }} />
            Weight Progress
          </ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formattedDate" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="weight" 
                  stroke="#007bff" 
                  strokeWidth={3}
                  dot={{ fill: '#007bff', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartCard>

        {/* Calories Progress Chart */}
        <ChartCard>
          <ChartTitle>
            <FaFire style={{ color: '#28a745' }} />
            Daily Calories
          </ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formattedDate" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="totalCalories" 
                  stroke="#28a745" 
                  fill="#28a745" 
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartCard>

        {/* Meal Distribution Chart */}
        <ChartCard>
          <ChartTitle>
            <FaChartBar style={{ color: '#ffc107' }} />
            Meal Distribution
          </ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={getMealDistributionData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {getMealDistributionData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartCard>

        {/* Meal Breakdown Chart */}
        <ChartCard>
          <ChartTitle>
            <FaUtensils style={{ color: '#6f42c1' }} />
            Meal Breakdown
          </ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formattedDate" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="breakfast" stackId="a" fill={COLORS[0]} name="Breakfast" />
                <Bar dataKey="lunch" stackId="a" fill={COLORS[1]} name="Lunch" />
                <Bar dataKey="dinner" stackId="a" fill={COLORS[2]} name="Dinner" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartCard>
      </ChartsGrid>
    </Container>
  );
};

export default ProgressDashboard;